import React, { useState } from 'react'
import styles from './ModuleSpoiler.module.scss'

/**
 * Collapsible module section (chevron + title). Closed by default.
 * Optional `actions` render next to the title; clicks there do not toggle open/close.
 * @param {{
 *   title: React.ReactNode,
 *   children: React.ReactNode,
 *   defaultExpanded?: boolean,
 *   theme?: 'default' | 'danger',
 *   className?: string,
 *   actions?: React.ReactNode | ((api: { expand: () => void, isExpanded: boolean }) => React.ReactNode),
 * }} props
 */
function ModuleSpoiler({
  title,
  children,
  defaultExpanded = false,
  theme = 'default',
  className = '',
  actions = null,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const expand = () => setIsExpanded(true)

  const renderedActions =
    typeof actions === 'function' ? actions({ expand, isExpanded }) : actions

  return (
    <div
      className={[
        styles.root,
        theme === 'danger' ? styles.rootDanger : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={[
            styles.toggle,
            theme === 'danger' ? styles.toggleDanger : '',
            isExpanded ? styles.toggleExpanded : '',
            isExpanded && theme === 'danger' ? styles.toggleDangerExpanded : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          <span className={isExpanded ? styles.chevronExpanded : styles.chevron}>▾</span>
          {title}
        </button>

        {renderedActions ? (
          <div
            className={styles.actions}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {renderedActions}
          </div>
        ) : null}
      </div>

      {isExpanded ? <div className={styles.body}>{children}</div> : null}
    </div>
  )
}

export default ModuleSpoiler
